/**
 * How a markdown step's price was rounded on the shelf (bata-reqs issue 38 R4, bata-platform ADR
 * 6190, `docs/adr/9067`).
 *
 * Under the market's `markdown.price_rounding_mode` the Zona Sale and outlet streams price a rung
 * on the IVA-inclusive shelf price and snap it to a price point. The run records, per step,
 * `rounding`: the mode, the ratified rung's share off the original (`target_depth`), the share the
 * shelf price actually takes off the original shelf price (`realised_depth`), the shelf price a till
 * charges, how it got there, and every rung merged on that price point. A step priced with no mode
 * in force — or written before the run recorded it — carries `null`, and nothing is shown: never a
 * target guessed from `depth_from_full_price`.
 *
 * Pure: no React, no clock.
 */

import type { PriceRounding } from '@/api/generated';
import { ROUNDING_DIRECTION_LABEL_KEY, roundingModeLabelKey } from '@/planning';

export interface RoundingPresentation {
  readonly mode: string;
  /** The mode in words, or `null` for a code this build does not know (shown verbatim). */
  readonly modeLabelKey: string | null;
  readonly targetDepth: number | null;
  readonly realisedDepth: number | null;
  /** Realised less target, in shares: positive when rounding deepened the cut. */
  readonly extraDepth: number | null;
  readonly shelfPrice: number | null;
  readonly direction: string | null;
  readonly directionLabelKey: string | null;
  /** Every ratified rung on this price point when two or more merged; else empty. */
  readonly merged: readonly number[];
}

const finite = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** A step's recorded rounding, ready to show; `null` when the run recorded none. */
export function roundingOf(rounding: PriceRounding | null | undefined): RoundingPresentation | null {
  if (rounding === null || rounding === undefined || rounding.mode === '') return null;
  const target = finite(rounding.target_depth);
  const realised = finite(rounding.realised_depth);
  const direction = rounding.direction ?? null;
  const merged = (rounding.rungs_merged ?? []).filter((depth) => Number.isFinite(depth));
  return {
    mode: rounding.mode,
    modeLabelKey: roundingModeLabelKey(rounding.mode),
    targetDepth: target,
    realisedDepth: realised,
    extraDepth: target === null || realised === null ? null : realised - target,
    shelfPrice: finite(rounding.shelf_price),
    direction,
    directionLabelKey: direction === null ? null : ROUNDING_DIRECTION_LABEL_KEY[direction] ?? null,
    merged: merged.length > 1 ? merged : [],
  };
}
